import PropTypes from 'prop-types'

import { W, Z, step } from '../springs'

/** Gemeinsame Bausteine der Szenen: Farben, Icons, Positionen, Helfer. */

export const INK = '#111111'
export const WHITE = '#FFFFFF'
export const BLUE = '#407BFF'

/** Position eines Kindes relativ zur Formmitte. */
export const at = (x, y, extra) => ({ left: x, top: y, ...extra })

// Alle Icons mit derselben Strichstärke
export function Icon({ d, size = 20, stroke = 'currentColor', width = 1.75 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d={d} />
    </svg>
  )
}

Icon.propTypes = {
  d: PropTypes.string.isRequired,
  size: PropTypes.number,
  stroke: PropTypes.string,
  width: PropTypes.number
}

export const I = {
  arrow: 'M5 12h14M13 6l6 6-6 6',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  plus: 'M12 5v14M5 12h14',
  mail: 'M4 6.5h16v11H4zM4.5 7l7.5 6 7.5-6',
  link: 'M9 15l6-6M10.5 6.5l1.2-1.2a4 4 0 0 1 5.7 5.7l-1.2 1.2M13.5 17.5l-1.2 1.2a4 4 0 0 1-5.7-5.7l1.2-1.2',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20a2 2 0 0 0 4 0',
  alert: 'M12 8v5M12 16.5v.5M12 3l9.5 17h-19z',
  users:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM3 20a6 6 0 0 1 12 0M16 4.5a3.5 3.5 0 0 1 0 6.5M18 14a5.5 5.5 0 0 1 3 6',
  template: 'M5 4h14v16H5zM8.5 8.5h7M8.5 12h7M8.5 15.5h4',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7.5V12l3 2',
  calendar: 'M4.5 6h15v14h-15zM4.5 10h15M8.5 3.5v4M15.5 3.5v4',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4'
}

export function Avatar({ name, size = 28, tone = 'blue', style, data }) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
  return (
    <span
      className={`mo-av mo-av--${tone}`}
      data-mo={data}
      style={{ width: size, height: size, fontSize: size * 0.38, ...style }}
    >
      {initials}
    </span>
  )
}

Avatar.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  tone: PropTypes.oneOf(['blue', 'ink', 'light']),
  style: PropTypes.object,
  data: PropTypes.string
}

/** Sichtbarkeit mit kurzem Blur, 0 bis 1. */
export function show(node, v, blur = 8) {
  if (!node) return
  node.style.opacity = v.toFixed(3)
  node.style.visibility = v < 0.002 ? 'hidden' : 'visible'
  node.style.filter =
    v > 0.995 ? 'none' : `blur(${((1 - v) * blur).toFixed(2)}px)`
}

/** Text, der Zeichen für Zeichen getippt wird. */
export const typed = (t, text, t0, per = 0.06) =>
  text.slice(
    0,
    Math.max(0, Math.min(text.length, Math.floor((t - t0) / per) + 1))
  )

/**
 * Indikator mit zwei Kanten auf eigenen Federn: Die Kante in Bewegungsrichtung
 * läuft auf der schnellen Feder, die hintere zieht nach. So dehnt er sich
 * beim Wechsel und zieht sich danach wieder zusammen.
 */
const FAST = [24, 0.86]
const SLOW = [11, 0.86]
export function edges(start, events) {
  const ls = []
  const rs = []
  let prev = start
  events.forEach((e) => {
    const right = (e.L + e.R) / 2 >= (prev.L + prev.R) / 2
    const [lw, lz] = right ? SLOW : FAST
    const [rw, rz] = right ? FAST : SLOW
    ls.push([e.t, e.L - prev.L, lw, lz])
    rs.push([e.t, e.R - prev.R, rw, rz])
    prev = e
  })
  const sum = (v0, list, t) =>
    list.reduce((v, [ts, d, w, z]) => v + d * step(t - ts, w, z), v0)
  return (t) => {
    const L = sum(start.L, ls, t)
    const R = sum(start.R, rs, t)
    return [L, Math.max(L + 4, R)]
  }
}

/** Ein Wert, der zu `t0` von 0 auf `to` federt. */
export const pop = (t, t0, to = 1, w = W, z = Z) => to * step(t - t0, w, z)

/** Position auf einer Skala von 0 bis 100 zwischen zwei x-Werten. */
export const lerpX = (a, b, v) => a + ((b - a) * v) / 100
